// src/components/TripSelector.jsx
import React, { useState, useEffect } from 'react';
import { MapPin, ChevronDown } from 'lucide-react';
import apiService from '../services/apiService';

const TripSelector = ({ selectedTripId, onTripSelect, placeholder = "Seleccionar viaje", required = false }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadTrips();
  }, []);

  const loadTrips = async () => {
    setLoading(true);
    try {
      const data = await apiService.trip.getAll();
      setTrips(data);
    } catch (err) {
      console.error('Error al cargar viajes:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedTrip = trips.find(trip => trip.id === selectedTripId);

  const handleTripSelect = (trip) => {
    onTripSelect(trip);
    setIsOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'planning': return 'Planificando';
      case 'active': return 'Activo';
      case 'completed': return 'Completado';
      default: return 'Desconocido';
    }
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 text-left border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
          selectedTrip 
            ? 'border-gray-300 bg-white' 
            : 'border-gray-300 bg-gray-50'
        } ${required && !selectedTrip ? 'border-red-300 bg-red-50' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin size={20} className="text-gray-400" />
            <div>
              {selectedTrip ? (
                <>
                  <p className="font-medium text-gray-800">{selectedTrip.name?.replace(/-$/, '')}</p>
                  {selectedTrip.startDate && selectedTrip.endDate && (
                    <p className="text-sm text-gray-500">
                      {new Date(selectedTrip.startDate).toLocaleDateString('es-CO')} - {new Date(selectedTrip.endDate).toLocaleDateString('es-CO')}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-gray-500">{placeholder}</p>
              )}
            </div>
          </div>
          <ChevronDown 
            size={20} 
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </button>

      {/* Error Message */}
      {required && !selectedTrip && (
        <p className="text-red-500 text-sm mt-1">Debes seleccionar un viaje</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Cargando viajes...</p>
            </div>
          ) : trips.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <MapPin size={24} className="mx-auto mb-2" />
              <p>No hay viajes disponibles</p>
            </div>
          ) : (
            <div className="py-2">
              {trips.map((trip) => (
                <button
                  key={trip.id}
                  onClick={() => handleTripSelect(trip)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{trip.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {trip.startDate && (
                          <span className="text-sm text-gray-500">
                            {new Date(trip.startDate).toLocaleDateString('es-CO')}
                          </span>
                        )}
                        {trip.endDate && (
                          <>
                            <span className="text-gray-400">-</span>
                            <span className="text-sm text-gray-500">
                              {new Date(trip.endDate).toLocaleDateString('es-CO')}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trip.status)}`}>
                      {getStatusText(trip.status)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default TripSelector;
